// src/components/Map/ItineraryLayerParking.tsx
import React, { useEffect, useMemo } from "react";
import { Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import IconMarker from "./IconItinerary";
import PopupItineraryParking from "./PopupItineraryParking";

export type ItineraryPoint = {
  LAT: number;
  LNG?: number;
  LON?: number;
  SOG?: number;
  ENGINESTAT?: number;
  distance?: number;
  ALT?: number;
  COG?: number;
  ALARMS?: string;
  PRIVATE_MODE?: number;
  TIMESTAMP?: string;
  TIMESTAMP_END?: string;
  GPSDIST?: number;
};

export interface Itinerary {
  id: number;
  start: string;
  end: string;
  name: string;
  distance: number;
  ALT: number;
  LAT: number;
  LNG: number;
  COG: number;
  SOG: number;
  ALARMS: string;
  PRIVATE_MODE: number;
}

export type ItineraryLayerDataProps = {
  points: ItineraryPoint[];
  visible?: boolean;
  color?: string;
  autoFit?: boolean;
};

const getLng = (p: ItineraryPoint): number | undefined => p.LNG ?? p.LON;

// NOM CORRIGÉ ICI (garde ton ancien nom !)
const ItineraryLayerDataParking: React.FC<ItineraryLayerDataProps> = ({
  points,
  visible = true,
  color = "#0ea5e9",
  autoFit = true,
}) => {
  const map = useMap();

  useEffect(() => {
    if (!visible || points.length === 0 || !autoFit) return;

    const latlngs = points
      .map((p) => {
        const lng = getLng(p);
        return lng !== undefined ? L.latLng(p.LAT, lng) : null;
      })
      .filter((l): l is L.LatLng => l !== null);

    if (latlngs.length > 0) {
      map.fitBounds(L.latLngBounds(latlngs), { padding: [50, 50] });
    }
  }, [points, visible, autoFit, map]);

  const polylinePositions = useMemo<[number, number][]>(() => {
    return points
      .map((p) => {
        const lng = getLng(p);
        return lng !== undefined ? [p.LAT, lng] : null;
      })
      .filter((p): p is [number, number] => p !== null);
  }, [points]);

  // Détection intelligente des vrais arrêts (plus de 200 marqueurs !)
  const parkingStops = useMemo(() => {
    const stops: { point: ItineraryPoint; lng: number; index: number }[] = [];

    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const lng = getLng(p);
      if (!lng || (p.SOG ?? 0) > 0.3) continue;

      const prev = points[i - 1];
      const next = points[i + 1];
      const prevStopped = prev && (prev.SOG ?? 0) <= 0.3;
      const nextStopped = next && (next.SOG ?? 0) <= 0.3;

      if (prevStopped || nextStopped || i === points.length - 1) {
        if (stops.length === 0 || i - stops[stops.length - 1].index > 10) {
          stops.push({ point: p, lng, index: i });
        }
      }
    }
    return stops;
  }, [points]);

  const handleItemClick = (item: Itinerary) => {
    console.log("Parking cliqué →", item);
  };

  if (!visible || polylinePositions.length === 0) return null;

  return (
    <>
      <Polyline
        positions={polylinePositions}
        pathOptions={{ color, weight: 5, opacity: 0.85 }}
      />

      {parkingStops.map(({ point, lng, index }) => {
        const engineRunning = point.ENGINESTAT !== 0;
        const stateName = engineRunning
          ? "Parking moteur allumé"
          : "Moteur arrêté";

        const itineraryData: Itinerary = {
          id: index,
          start: point.TIMESTAMP ?? "",
          end: point.TIMESTAMP_END ?? "",
          name: stateName,
          distance: point.GPSDIST ? point.GPSDIST / 1000 : point.distance ?? 0,
          ALT: point.ALT ?? 0,
          LAT: point.LAT,
          LNG: lng,
          COG: point.COG ?? 0,
          SOG: 0,
          ALARMS: point.ALARMS ?? "",
          PRIVATE_MODE: point.PRIVATE_MODE ?? 0,
        };

        return (
          <Marker
            key={`parking-${index}-${point.TIMESTAMP || index}`}
            position={[point.LAT, lng]}
            icon={IconMarker(
              itineraryData.COG,
              0,
              itineraryData.name,
              () => handleItemClick(itineraryData)
            )}
          >
            <PopupItineraryParking itinerary={itineraryData} />
          </Marker>
        );
      })}
    </>
  );
};

export default ItineraryLayerDataParking; // export avec le bon nom